import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/auth-context';
import { bankingApi, type BankAccount } from '../lib/api/banking';

// Helper function to decode JWT payload (browser-compatible)
const decodeJwtPayload = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};
import { 
  Wallet, 
  FileText, 
  Calendar, 
  DollarSign, 
  AlertCircle,
  CheckCircle,
  Clock,
  Search,
  Filter,
  Download,
  Eye,
  CreditCard,
  Edit,
  Trash2,
  Plus,
  CheckCircle2,
  XCircle,
  Users,
  TrendingUp,
  AlertTriangle,
  FileCheck,
  Receipt,
  Zap,
  Settings,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { MoonLoader } from '../components/ui/moon-loader';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { format } from 'date-fns';
import { PageLayout } from '../components/page-layout';
import { companiesApi, purchaseApi } from '../lib/api/accounting';
import { getCompanyId, getTenantId, getApiUrl } from '../lib/config';
import { VendorModal } from '../components/vendor-modal';
import { toast } from 'sonner';
import { TranslatedText } from '../components/translated-text';
import { useTranslation, useTranslatedText } from '../hooks/useTranslation';

// Use the same API configuration as the rest of the app
const getApiBaseUrl = () => {
  return getApiUrl('');
};

// Mock API functions - Replace with actual API calls
const fetchInvoices = async (companyId: string) => {
  // Use the token and extract tenant ID from token
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  
  if (!token) {
    console.error('No token found');
    return { data: { invoices: [] } };
  }
  
  let tenantId;
  try {
    const payload = decodeJwtPayload(token);
    tenantId = payload.tenantId;
  } catch (error) {
    console.error('Error parsing token:', error);
    return { data: { invoices: [] } };
  }
  
  
  const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices?companyId=${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'x-company-id': companyId
    }
  });
  
  const data = await response.json();
  return data;
};

const fetchBills = async (companyId: string) => {
  // Use the token and extract tenant ID from token
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  
  if (!token) {
    console.error('No token found');
    return { data: { bills: [] } };
  }
  
  let tenantId;
  try {
    const payload = decodeJwtPayload(token);
    tenantId = payload.tenantId;
  } catch (error) {
    console.error('Error parsing token:', error);
    return { data: { bills: [] } };
  }
  
  
  const response = await fetch(`${getApiBaseUrl()}/api/bills?companyId=${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'x-company-id': companyId
    }
  });
  
  const data = await response.json();
  return data;
};

// Use existing purchaseApi.getVendors instead of custom fetch

const fetchDashboard = async (companyId: string) => {
  // Use the token and extract tenant ID from token
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  
  if (!token) {
    console.error('No token found');
    return { data: { summary: {} } };
  }
  
  let tenantId;
  try {
    const payload = decodeJwtPayload(token);
    tenantId = payload.tenantId;
  } catch (error) {
    console.error('Error parsing token:', error);
    return { data: { summary: {} } };
  }
  
  
  const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/dashboard?companyId=${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'x-company-id': companyId
    }
  });
  
  const data = await response.json();
  return data;
};

const fetchAgingReport = async (companyId: string) => {
  // Use the token and extract tenant ID from token
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  
  if (!token) {
    console.error('No token found');
    return { data: {} };
  }
  
  let tenantId;
  try {
    const payload = decodeJwtPayload(token);
    tenantId = payload.tenantId;
  } catch (error) {
    console.error('Error parsing token:', error);
    return { data: {} };
  }
  
  
  const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/aging-report?companyId=${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'x-company-id': companyId
    }
  });
  
  const data = await response.json();
  return data;
};

const fetchPaymentData = async (companyId: string) => {
  // Use the token and extract tenant ID from token
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  
  if (!token) {
    console.error('No token found');
    return { data: { payments: [], schedules: [], bankAccounts: [], stats: {} } };
  }
  
  let tenantId;
  try {
    const payload = decodeJwtPayload(token);
    tenantId = payload.tenantId;
  } catch (error) {
    console.error('Error parsing token:', error);
    return { data: { payments: [], schedules: [], bankAccounts: [], stats: {} } };
  }
  
  
  const response = await fetch(`${getApiBaseUrl()}/api/payments/payments?companyId=${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'x-company-id': companyId
    }
  });
  
  const data = await response.json();
  return data;
};

// List of banks operating in Rwanda for receiver bank selection
const AVAILABLE_BANKS = [
  'Bank of Kigali',
  'Equity Bank',
  'Ecobank Rwanda',
  'I&M Bank Rwanda',
  'Bank Populaire du Rwanda',
  'Access Bank Rwanda',
  'GT Bank Rwanda',
  'Urwego Bank',
  'Zigama Credit and Savings Bank',
  'Development Bank of Rwanda'
];

// Fetch bank accounts for payment processing
const fetchBankAccounts = async (companyId: string) => {
  try {
    const accounts = await bankingApi.getBankAccounts(companyId);
    return accounts || [];
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error);
    return [];
  }
};

export default function EnhancedAccountsPayable() {
  const { tSync } = useTranslation();
  // Use useTranslatedText for reactive tab labels
  const overviewLabel = useTranslatedText('Overview');
  const invoiceCaptureLabel = useTranslatedText('Invoice Capture');
  const paymentManagementLabel = useTranslatedText('Payment Management');
  const reportsAnalyticsLabel = useTranslatedText('Reports & Analytics');
  const searchInvoicesPlaceholder = useTranslatedText('Search invoices...');
  const searchBillsPlaceholder = useTranslatedText('Search bills...');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [isMatchingDialogOpen, setIsMatchingDialogOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [selectedBillForView, setSelectedBillForView] = useState<any>(null);
  const [showBillViewDialog, setShowBillViewDialog] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false)
  const [billReconciliationStatus, setBillReconciliationStatus] = useState<{
    isReconciled: boolean
    reconciledPayments: number
    totalPayments: number
    bankTransactions: Array<{ id: string; amount: number; date: string; description: string }>
  } | null>(null);

  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const authReady = !authLoading;

  // Fetch companies first
  const { data: companiesData, isLoading: companiesLoading, error: companiesError } = useQuery({
    queryKey: ['companies'],
    queryFn: companiesApi.getCompanies,
    enabled: authReady
  });

  // Get company ID from localStorage or use seed-company-1 as default
  const firstCompanyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || 'seed-company-1';


  // Fetch data
  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', firstCompanyId],
    queryFn: () => fetchInvoices(firstCompanyId),
    enabled: authReady && !!firstCompanyId
  });

  const { data: billsData, isLoading: billsLoading } = useQuery({
    queryKey: ['bills', firstCompanyId],
    queryFn: () => fetchBills(firstCompanyId),
    enabled: authReady && !!firstCompanyId
  });

  const { data: vendorsData, isLoading: vendorsLoading, error: vendorsError } = useQuery({
    queryKey: ['vendors', firstCompanyId],
    queryFn: async () => {
      try {
        const result = await purchaseApi.getVendors(firstCompanyId);
        // Ensure we always return an array
        if (Array.isArray(result)) {
          return result;
        }
        // Handle different response formats
        const resultAny = result as any;
        if (resultAny?.items && Array.isArray(resultAny.items)) {
          return resultAny.items;
        }
        if (resultAny?.data && Array.isArray(resultAny.data)) {
          return resultAny.data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching vendors:', error);
        // Return mock vendors for demo purposes
        return [
          { id: 'demo-vendor-1', name: 'Demo Vendor 1', email: 'vendor1@demo.com' },
          { id: 'demo-vendor-2', name: 'Demo Vendor 2', email: 'vendor2@demo.com' },
          { id: 'demo-vendor-3', name: 'Demo Vendor 3', email: 'vendor3@demo.com' }
        ];
      }
    },
    enabled: authReady && !!firstCompanyId
  });

  // Ensure vendors is always an array
  const vendors = Array.isArray(vendorsData) ? vendorsData : [];

  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['ap-dashboard', firstCompanyId],
    queryFn: () => fetchDashboard(firstCompanyId),
    enabled: authReady && !!firstCompanyId
  });

  const { data: agingData, isLoading: agingLoading } = useQuery({
    queryKey: ['ap-aging', firstCompanyId],
    queryFn: () => fetchAgingReport(firstCompanyId),
    enabled: authReady && !!firstCompanyId
  });

  const { data: paymentData, isLoading: paymentDataLoading } = useQuery({
    queryKey: ['payment-data', firstCompanyId],
    queryFn: () => fetchPaymentData(firstCompanyId),
    enabled: authReady && !!firstCompanyId
  });

  // Helper function to extract tenant ID from token and company ID from localStorage
  const getTokenData = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId');
    
    if (!token) return { tenantId: null, companyId };
    
    try {
      const payload = decodeJwtPayload(token);
      return {
        tenantId: payload.tenantId,
        companyId: companyId
      };
    } catch (error) {
      console.error('Error parsing token:', error);
      return { tenantId: null, companyId };
    }
  };


  // Fetch purchase orders for invoice creation
  const { data: purchaseOrdersData, isLoading: purchaseOrdersLoading } = useQuery({
    queryKey: ['purchase-orders', firstCompanyId],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const { tenantId } = getTokenData();
        
        if (!tenantId || !firstCompanyId) {
          console.error('Missing tenant or company ID');
          return { items: [] };
        }
        
        
        const response = await fetch(`${getApiBaseUrl()}/api/purchase-orders?companyId=${firstCompanyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          }
        });
        
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error fetching purchase orders:', error);
        return { items: [] };
      }
    },
    enabled: authReady && !!firstCompanyId
  });

  const invoices = (invoicesData as any)?.data?.invoices || [];
  const bills = (billsData as any)?.data?.bills || (billsData as any)?.data?.items || (billsData as any)?.items || [];
  const dashboard = (dashboardData as any)?.data?.summary || {};
  const agingReport = (agingData as any)?.data || {};
  const payments = (paymentData as any)?.data?.payments || [];
  const paymentSchedules = (paymentData as any)?.data?.schedules || [];
  const bankAccounts = (paymentData as any)?.data?.bankAccounts || [];
  const paymentStats = (paymentData as any)?.data?.stats || {};
  const purchaseOrders = (purchaseOrdersData as any)?.items || (purchaseOrdersData as any)?.data?.items || [];
  
  const mappedPurchaseOrders = purchaseOrders.map((po: any) => ({
    id: po.id,
    poNumber: po.poNumber,
    status: po.status,
    totalAmount: po.totalAmount,
    relatedBillId: po.relatedBillId,
    hasRelatedBill: !!po.relatedBill,
    relatedBill: po.relatedBill ? { id: po.relatedBill.id, billNumber: po.relatedBill.billNumber } : null,
    vendor: po.vendor?.name
  }));

  // Helper function to get default form values
  const getDefaultInvoiceForm = () => ({
    vendorId: '',
    purchaseOrderId: '',
    invoiceNumber: '',
    invoiceDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    totalAmount: '',
    subtotal: '',
    taxAmount: '',
    currency: 'Frw',
    source: 'manual',
    notes: ''
  });

  // Form states
  const [invoiceForm, setInvoiceForm] = useState(getDefaultInvoiceForm());

  // Calculate subtotal when total amount or tax percentage changes
  useEffect(() => {
    if (invoiceForm.totalAmount && !invoiceForm.subtotal) {
      const totalAmount = parseFloat(invoiceForm.totalAmount);
      const taxPercentage = invoiceForm.taxAmount ? parseFloat(invoiceForm.taxAmount) : 0;
      const subtotal = totalAmount / (1 + (taxPercentage / 100));
      
      console.log('🔍 useEffect - Auto calc subtotal:', {
        totalAmount,
        taxAmount: invoiceForm.taxAmount,
        taxPercentage,
        subtotal: subtotal.toFixed(2)
      });
      
      setInvoiceForm(prev => ({
        ...prev,
        subtotal: subtotal.toFixed(2)
      }));
    }
  }, [invoiceForm.totalAmount, invoiceForm.taxAmount]);

  const [matchingForm, setMatchingForm] = useState({
    purchaseOrderId: '',
    goodsReceivedNoteId: '',
    matchingType: 'two_way'
  });

  const [approvalForm, setApprovalForm] = useState({
    approverId: 'current-user', // Default approver ID
    approvalLevel: 1,
    comments: '',
    status: 'approved'
  });

  const [paymentForm, setPaymentForm] = useState({
    billId: '',
    scheduledDate: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    paymentMethod: 'bank_transfer',
    payerBankAccountId: '', // Our company's bank account (payer)
    receiverBankAccountId: '', // Vendor's bank account (receiver)
    receiverBankName: '', // Vendor's bank name
    receiverAccountNumber: '', // Vendor's account number
    receiverRoutingNumber: '', // Vendor's routing number
    priority: 'normal',
    notes: ''
  });

  // Bank account state management
  const [availableBankAccounts, setAvailableBankAccounts] = useState<BankAccount[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  
  // Bank search state
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [filteredBanks, setFilteredBanks] = useState<string[]>(AVAILABLE_BANKS);
  
  // Track which bill is currently being processed
  const [processingBillId, setProcessingBillId] = useState<string | null>(null);

  // Load bank accounts when payment dialog opens
  const loadBankAccounts = async () => {
    const companyId = localStorage.getItem('company_id') || localStorage.getItem('companyId') || 'seed-company-1';
    if (!companyId) return;
    
    setLoadingBankAccounts(true);
    try {
      const accounts = await fetchBankAccounts(companyId);
      setAvailableBankAccounts(accounts);
      
      // Auto-select first account if none selected and payment method requires it
      if (accounts.length > 0 && !paymentForm.payerBankAccountId && 
          ['bank_transfer', 'check'].includes(paymentForm.paymentMethod)) {
        setPaymentForm(prev => ({ ...prev, payerBankAccountId: accounts[0].id }));
      }
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  // Check if selected bank account has sufficient balance
  const checkBankAccountBalance = () => {
    if (!paymentForm.payerBankAccountId || !paymentForm.amount) return true;
    
    const selectedAccount = availableBankAccounts.find(account => account.id === paymentForm.payerBankAccountId);
    if (!selectedAccount || !selectedAccount.balance) return true;
    
    const accountBalance = Number(selectedAccount.balance);
    const paymentAmount = Number(paymentForm.amount);
    
    return accountBalance >= paymentAmount;
  };

  // Get selected bank account balance
  const getSelectedBankAccountBalance = () => {
    if (!paymentForm.payerBankAccountId) return null;
    
    const selectedAccount = availableBankAccounts.find(account => account.id === paymentForm.payerBankAccountId);
    return selectedAccount?.balance ? Number(selectedAccount.balance) : null;
  };

  // Filter banks based on search term
  const filterBanks = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredBanks(AVAILABLE_BANKS);
      return;
    }
    
    const filtered = AVAILABLE_BANKS.filter(bank =>
      bank.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBanks(filtered);
  };

  // Handle bank search
  const handleBankSearch = (value: string) => {
    setBankSearchTerm(value);
    filterBanks(value);
  };

  // Filter data
  const filteredInvoices = invoices.filter((invoice: any) => {
    const matchesSearch = !searchTerm || 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.vendor?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredBills = bills.filter((bill: any) => {
    const matchesSearch = !searchTerm || 
      bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.vendor?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  
  // Debug invoices data
  
  // Debug individual invoice structure
  if (invoices.length > 0) {
  }
  

  // Mutations
  const captureInvoiceMutation = useMutation({
    mutationFn: async (data: any) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      let tenantId;
      try {
        const payload = decodeJwtPayload(token);
        tenantId = payload.tenantId;
      } catch (error) {
        console.error('Error parsing token:', error);
        throw new Error('Invalid token format');
      }
      
      console.log('Creating invoice with:', { tenantId, companyId: firstCompanyId, data });
      console.log('📤 Invoice data being sent:', JSON.stringify(data, null, 2));
      
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(data)
      });
      
      console.log('Invoice creation response status:', response.status);
      console.log('Invoice creation response headers:', Object.fromEntries(response.headers.entries()));
      
      const result = await response.json();
      console.log('Invoice creation response:', result);
      
      if (!response.ok) {
        // Enhanced error handling for different status codes
        let errorMessage = `HTTP ${response.status}`;
        
        if (response.status === 400) {
          // Bad Request - validation errors
          if (result.details && Array.isArray(result.details)) {
            const validationErrors = result.details.map((detail: any) => 
              `${detail.field || detail.path?.join('.')}: ${detail.message}`
            ).join(', ');
            errorMessage = `Validation failed: ${validationErrors}`;
          } else if (result.message) {
            errorMessage = result.message;
          } else {
            errorMessage = 'Invalid data provided. Please check all fields.';
          }
        } else if (response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        console.error('❌ Invoice creation failed:', {
          status: response.status,
          error: result,
          data: data
        });
        
        throw new Error(errorMessage);
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice captured successfully');
      setIsInvoiceDialogOpen(false);
      resetInvoiceForm(); // Clear form after successful creation
    },
    onError: (error: any) => {
      console.error('❌ Invoice capture mutation error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Show detailed error message
      let errorMessage = 'Failed to capture invoice';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  });

  const matchInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, data }: { invoiceId: string, data: any }) => {
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices/${invoiceId}/match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('auth_token')}`,
          'x-tenant-id': localStorage.getItem('tenant_id') || 'demo-tenant',
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(data)
      });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice matched successfully');
      setIsMatchingDialogOpen(false);
    }
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, data }: { invoiceId: string, data: any }) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      let tenantId;
      try {
        const payload = decodeJwtPayload(token);
        tenantId = payload.tenantId;
      } catch (error) {
        console.error('Error parsing token:', error);
        throw new Error('Invalid token');
      }
      
      console.log('Updating invoice with:', { tenantId, companyId: firstCompanyId, invoiceId, data });
      
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(data)
      });
      
      console.log('Update response status:', response.status);
      const result = await response.json();
      console.log('Update response:', result);
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}`);
      }
      
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice updated successfully!');
      setIsInvoiceDialogOpen(false);
      resetInvoiceForm(); // Clear form after successful update
      console.log('Update successful, data:', data);
    },
    onError: (error: any) => {
      console.error('Update failed:', error);
      toast.error('Failed to update invoice: ' + (error?.message || 'Unknown error'));
    }
  });

  // Create vendor mutation
  const createVendorMutation = useMutation({
    mutationFn: async (vendorData: any) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const tenantId = getTenantId()
      const response = await fetch(
        getApiUrl('api/enhanced-transaction-processing/vendors'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': firstCompanyId
          },
          body: JSON.stringify(vendorData)
        }
      )
      const data = await response.json()
      if (!data.success) {
        // Handle duplicate vendor error specifically
        if (data.error === 'Vendor already exists' || data.message?.includes('already exists')) {
          throw new Error(data.message || 'A vendor with this name already exists for this company.')
        }
        throw new Error(data.message || data.error || 'Failed to create vendor')
      }
      return data.data
    },
    onSuccess: (newVendor) => {
      // Invalidate vendors query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vendors', firstCompanyId] })
      setIsVendorModalOpen(false)
      // Select the newly created vendor
      setInvoiceForm({...invoiceForm, vendorId: newVendor.id})
      toast.success('Vendor created successfully')
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to create vendor'
      toast.error(errorMessage)
    }
  })

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      let tenantId;
      try {
        const payload = decodeJwtPayload(token);
        tenantId = payload.tenantId;
      } catch (error) {
        console.error('Error parsing token:', error);
        throw new Error('Invalid token');
      }
      
      console.log('Deleting invoice:', { tenantId, companyId: firstCompanyId, invoiceId });
      
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'x-company-id': firstCompanyId
        }
      });
      
      console.log('Delete response status:', response.status);
      const result = await response.json();
      console.log('Delete response:', result);
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}`);
      }
      
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted successfully!');
      console.log('Delete successful, data:', data);
    },
    onError: (error: any) => {
      console.error('Delete failed:', error);
      toast.error('Failed to delete invoice: ' + (error?.message || 'Unknown error'));
    }
  });

  const approveInvoiceMutation = useMutation({
    mutationFn: async ({ invoiceId, data }: { invoiceId: string, data: any }) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      let tenantId;
      try {
        const payload = decodeJwtPayload(token);
        tenantId = payload.tenantId;
      } catch (error) {
        console.error('Error parsing token:', error);
        throw new Error('Invalid token');
      }
      
      console.log('Approving invoice with:', { tenantId, companyId: firstCompanyId, invoiceId, data });
      
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/invoices/${invoiceId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(data)
      });
      
      console.log('Approval response status:', response.status);
      const result = await response.json();
      console.log('Approval response:', result);
      
      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}`);
      }
      
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Invoice approved successfully! Bill created.');
      setIsApprovalDialogOpen(false);
      console.log('Approval successful, data:', data);
    },
    onError: (error: any) => {
      console.error('Approval failed:', error);
      toast.error('Failed to approve invoice: ' + (error?.message || 'Unknown error'));
    }
  });

  const processPaymentMutation = useMutation({
    mutationFn: async (paymentData: any) => {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No token found');
      }
      
      let tenantId;
      try {
        const payload = decodeJwtPayload(token);
        tenantId = payload.tenantId;
      } catch (error) {
        console.error('Error parsing token:', error);
        throw new Error('Invalid token');
      }
      
      console.log('Processing payment with:', { tenantId, companyId: firstCompanyId, paymentData });
      
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/payments/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(paymentData)
      });
      
      console.log('Payment processing response status:', response.status);
      
      // Parse response
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        // If response is not JSON, check status
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to process payment`);
        }
        // If response is ok but not JSON, return success
        return { success: true, message: 'Payment processed successfully' };
      }
      
      console.log('Payment processing response:', result);
      
      // Check if response indicates success
      if (!response.ok) {
        // Check if result has success field
        if (result.success === false) {
          throw new Error(result.error || result.message || `HTTP ${response.status}`);
        }
        throw new Error(result.error || result.message || `HTTP ${response.status}`);
      }
      
      // If response is ok but result.success is false, still throw error
      if (result.success === false) {
        throw new Error(result.error || result.message || 'Payment processing failed');
      }
      
      return result;
    },
    onMutate: async (paymentData) => {
      // Set the bill ID that's being processed
      if (paymentData.billId) {
        setProcessingBillId(paymentData.billId);
      }
    },
    onSuccess: async (data) => {
      console.log('Payment processed successfully, data:', data);
      
      // Update bank account balance if payment method requires it
      if (paymentForm.payerBankAccountId && ['bank_transfer', 'check'].includes(paymentForm.paymentMethod)) {
        try {
          console.log('Updating bank account balance:', {
            bankAccountId: paymentForm.payerBankAccountId,
            amount: paymentForm.amount,
            operation: 'debit'
          });
          
          const balanceUpdateResponse = await bankingApi.updateBankAccountBalance(
            paymentForm.payerBankAccountId,
            parseFloat(paymentForm.amount),
            'debit',
            `Payment for bill ${paymentForm.billId}`
          );
          
          console.log('Bank balance updated successfully:', balanceUpdateResponse);
          
          // Invalidate bank accounts query to refresh balance
          queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
          queryClient.invalidateQueries({ queryKey: ['payment-data'] });
          
        } catch (error: any) {
          console.error('Failed to update bank account balance:', error);
          // Don't show error toast if payment was successful - just log it
          // The payment was processed successfully, bank balance update is secondary
          console.warn('Payment processed successfully but bank balance update failed:', error?.message);
        }
      }
      
      // Reset payment form
      setPaymentForm({
        billId: '',
        scheduledDate: format(new Date(), 'yyyy-MM-dd'),
        amount: '',
        paymentMethod: 'bank_transfer',
        payerBankAccountId: '',
        receiverBankAccountId: '',
        receiverBankName: '',
        receiverAccountNumber: '',
        receiverRoutingNumber: '',
        priority: 'normal',
        notes: ''
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      
      // Show success message
      toast.success('Payment processed successfully!');
      
      // Close modal
      setIsPaymentDialogOpen(false);
      setProcessingBillId(null); // Clear processing state
    },
    onError: (error: any) => {
      console.error('Payment processing failed:', error);
      toast.error('Failed to process payment: ' + (error?.message || 'Unknown error'));
      setProcessingBillId(null); // Clear processing state on error
    }
  });

  const schedulePaymentMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`${getApiBaseUrl()}/api/accounts-payable/payment-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('auth_token')}`,
          'x-tenant-id': localStorage.getItem('tenant_id') || 'demo-tenant',
          'x-company-id': firstCompanyId
        },
        body: JSON.stringify(data)
      });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast.success('Payment scheduled successfully');
      setIsPaymentDialogOpen(false);
    }
  });

  const populateInvoiceForm = (invoice: any) => {
    setInvoiceForm({
      vendorId: invoice.vendorId || '',
      invoiceNumber: invoice.invoiceNumber || '',
      invoiceDate: invoice.invoiceDate || '',
      dueDate: invoice.dueDate || '',
      totalAmount: invoice.totalAmount ? parseFloat(invoice.totalAmount).toFixed(2) : '',
      subtotal: invoice.subtotal ? parseFloat(invoice.subtotal).toFixed(2) : '',
      taxAmount: invoice.taxAmount ? parseFloat(invoice.taxAmount).toFixed(2) : '',
      currency: invoice.currency || 'Frw',
      source: invoice.source || 'manual',
      notes: invoice.notes || '',
      purchaseOrderId: '' // Will be extracted from rawData if available
    });
  };

  const resetInvoiceForm = () => {
    setInvoiceForm(getDefaultInvoiceForm());
    setSelectedInvoice(null);
    setFormErrors({}); // Clear form errors
  };

  const handleCaptureInvoice = () => {
    // Check authentication first
    if (!isAuthenticated) {
      toast.error('Please log in to capture invoices');
      return;
    }

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Authentication token not found. Please log in again.');
      return;
    }

    const errors: {[key: string]: string} = {};
    
    // Enhanced validation to match backend schema requirements
    
    // 1. Validate vendorId (required, must be valid UUID)
    if (!invoiceForm.vendorId || invoiceForm.vendorId === 'loading-vendors' || invoiceForm.vendorId === 'no-vendors') {
      errors.vendorId = 'Please select a vendor';
    } else if (typeof invoiceForm.vendorId !== 'string' || invoiceForm.vendorId.trim() === '') {
      errors.vendorId = 'Vendor ID must be a valid string';
    }
    
    // 2. Validate invoiceNumber (required, non-empty string)
    if (!invoiceForm.invoiceNumber || invoiceForm.invoiceNumber.trim() === '') {
      errors.invoiceNumber = 'Please enter an invoice number';
    } else if (typeof invoiceForm.invoiceNumber !== 'string') {
      errors.invoiceNumber = 'Invoice number must be a valid string';
    }
    
    // 3. Validate invoiceDate (required, valid date)
    if (!invoiceForm.invoiceDate) {
      errors.invoiceDate = 'Please select an invoice date';
    } else {
      const invoiceDate = new Date(invoiceForm.invoiceDate);
      if (isNaN(invoiceDate.getTime())) {
        errors.invoiceDate = 'Please enter a valid date';
      }
    }
    
    // 4. Validate dueDate (optional, but if provided must be valid date)
    if (invoiceForm.dueDate) {
      const dueDate = new Date(invoiceForm.dueDate);
      if (isNaN(dueDate.getTime())) {
        errors.dueDate = 'Please enter a valid due date';
      }
    }
    
    // 5. Validate totalAmount (required, positive number)
    if (!invoiceForm.totalAmount || invoiceForm.totalAmount.trim() === '') {
      errors.totalAmount = 'Please enter a total amount';
    } else {
      const totalAmount = parseFloat(invoiceForm.totalAmount);
      if (isNaN(totalAmount)) {
        errors.totalAmount = 'Total amount must be a valid number';
      } else if (totalAmount <= 0) {
        errors.totalAmount = 'Total amount must be greater than 0';
      }
    }
    
    // 6. Validate subtotal (calculated, should be valid)
    if (!invoiceForm.subtotal || invoiceForm.subtotal.trim() === '') {
      errors.subtotal = 'Subtotal calculation failed';
    } else {
      const subtotal = parseFloat(invoiceForm.subtotal);
      if (isNaN(subtotal)) {
        errors.subtotal = 'Subtotal must be a valid number';
      } else if (subtotal < 0) {
        errors.subtotal = 'Subtotal cannot be negative';
      }
    }
    
    // 7. Validate taxAmount (optional percentage, but if provided must be valid)
    if (invoiceForm.taxAmount && invoiceForm.taxAmount.trim() !== '') {
      const taxPercentage = parseFloat(invoiceForm.taxAmount);
      if (isNaN(taxPercentage)) {
        errors.taxAmount = 'Tax percentage must be a valid number';
      } else if (taxPercentage < 0) {
        errors.taxAmount = 'Tax percentage cannot be negative';
      } else if (taxPercentage > 100) {
        errors.taxAmount = 'Tax percentage cannot exceed 100%';
      }
    }
    
    // 8. Validate currency (required, at least 3 characters)
    if (!invoiceForm.currency || invoiceForm.currency.trim() === '') {
      errors.currency = 'Please select a currency';
    } else if (invoiceForm.currency.length < 3) {
      errors.currency = 'Currency must be at least 3 characters (e.g., Frw, USD)';
    }
    
    // 9. Validate source (required, must be one of allowed values)
    const allowedSources = ['manual', 'email', 'api', 'ocr', 'upload'];
    if (!invoiceForm.source || invoiceForm.source.trim() === '') {
      errors.source = 'Please select a source';
    } else if (!allowedSources.includes(invoiceForm.source)) {
      errors.source = `Source must be one of: ${allowedSources.join(', ')}`;
    }
    
    // 10. Validate notes (required, must be provided)
    if (!invoiceForm.notes || invoiceForm.notes.trim() === '') {
      errors.notes = 'Please enter notes';
    } else if (typeof invoiceForm.notes !== 'string') {
      errors.notes = 'Notes must be a valid string';
    } else {
      const notesLength = invoiceForm.notes.trim().length;
      if (notesLength > 1000) {
        errors.notes = 'Notes cannot exceed 1000 characters';
      } else if (notesLength < 3) {
        errors.notes = 'Notes must be at least 3 characters';
      }
    }
    
    // 11. Validate purchase order if selected
    if (invoiceForm.purchaseOrderId && (invoiceForm.purchaseOrderId === 'loading' || invoiceForm.purchaseOrderId === 'no-pos')) {
      errors.purchaseOrderId = 'Please select a valid purchase order';
    }
    
    // 12. Auto-calculate total amount (no cross-field validation needed since it's auto-calculated)
    
    // Set errors and return if any validation fails
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form validation failed:', errors);
      return;
    }
    
    // Transform the form data to match the backend schema with proper type conversion
    const invoiceData = {
      vendorId: String(invoiceForm.vendorId).trim(),
      invoiceNumber: String(invoiceForm.invoiceNumber).trim(),
      invoiceDate: invoiceForm.invoiceDate, // Already validated as valid date
      dueDate: invoiceForm.dueDate ? invoiceForm.dueDate : null,
      totalAmount: Number(parseFloat(invoiceForm.totalAmount)), // Ensure it's a number
      subtotal: Number(parseFloat(invoiceForm.subtotal)), // Use calculated subtotal
      taxAmount: Number(parseFloat(invoiceForm.totalAmount)) - Number(parseFloat(invoiceForm.subtotal)), // Calculate actual tax amount
      currency: String(invoiceForm.currency).toUpperCase(), // Ensure uppercase
      source: String(invoiceForm.source), // Already validated against allowed values
      notes: invoiceForm.notes?.trim() || null,
      // Include purchase order reference if available
      ...(invoiceForm.purchaseOrderId && 
          invoiceForm.purchaseOrderId !== 'loading' && 
          invoiceForm.purchaseOrderId !== 'no-pos' && 
          { purchaseOrderId: String(invoiceForm.purchaseOrderId).trim() })
    };
    
    // Final validation of transformed data
    console.log('📤 Sending invoice data:', invoiceData);
    
    // Additional safety checks
    if (typeof invoiceData.vendorId !== 'string' || invoiceData.vendorId === '') {
      console.error('❌ Invalid vendorId after transformation:', invoiceData.vendorId);
      setFormErrors({ vendorId: 'Invalid vendor selected' });
      return;
    }
    
    if (typeof invoiceData.totalAmount !== 'number' || invoiceData.totalAmount <= 0) {
      console.error('❌ Invalid totalAmount after transformation:', invoiceData.totalAmount);
      setFormErrors({ totalAmount: 'Invalid total amount' });
      return;
    }
    
    if (typeof invoiceData.subtotal !== 'number' || invoiceData.subtotal < 0) {
      console.error('❌ Invalid subtotal after transformation:', invoiceData.subtotal);
      setFormErrors({ subtotal: 'Invalid subtotal amount' });
      return;
    }
    
    if (invoiceData.taxAmount !== undefined && (typeof invoiceData.taxAmount !== 'number' || invoiceData.taxAmount < 0)) {
      console.error('❌ Invalid taxAmount after transformation:', invoiceData.taxAmount);
      setFormErrors({ taxAmount: 'Invalid tax amount' });
      return;
    }
    
    // Check if we're editing an existing invoice
    if (selectedInvoice && selectedInvoice.id) {
      // Update existing invoice
      updateInvoiceMutation.mutate({ invoiceId: selectedInvoice.id, data: invoiceData });
    } else {
      // Create new invoice
      captureInvoiceMutation.mutate(invoiceData);
    }
  };

  const handleMatchInvoice = () => {
    if (selectedInvoice) {
      matchInvoiceMutation.mutate({ invoiceId: selectedInvoice.id, data: matchingForm });
    }
  };

  const handleApproveInvoice = () => {
    if (selectedInvoice) {
      approveInvoiceMutation.mutate({ invoiceId: selectedInvoice.id, data: approvalForm });
    }
  };

  const handleSchedulePayment = () => {
    schedulePaymentMutation.mutate(paymentForm);
  };

  const handleProcessPayment = () => {
    console.log('Payment form state:', paymentForm);
    
    // Validate payment form
    if (!paymentForm.billId) {
      toast.error('No bill selected for payment');
      return;
    }
    
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }
    
    if (!paymentForm.paymentMethod) {
      toast.error('Please select a payment method');
      return;
    }
    
    // Validate bank account selection for methods that require it
    if (['bank_transfer', 'check'].includes(paymentForm.paymentMethod)) {
      if (!paymentForm.payerBankAccountId) {
        toast.error('Please select a payer bank account (your company\'s bank account)');
        return;
      }
      if (!paymentForm.receiverBankName || !paymentForm.receiverAccountNumber) {
        toast.error('Please enter the receiver bank details (vendor\'s bank name and account number)');
        return;
      }
      
      // Check if bank account has sufficient balance
      if (!checkBankAccountBalance()) {
        const selectedAccount = availableBankAccounts.find(account => account.id === paymentForm.payerBankAccountId);
        const accountBalance = selectedAccount?.balance ? Number(selectedAccount.balance) : 0;
        const paymentAmount = Number(paymentForm.amount);
        
        toast.error(`Insufficient balance! Account has ${accountBalance.toLocaleString()} Frw but payment requires ${paymentAmount.toLocaleString()} Frw`);
        return;
      }
    }
    
    // Process payment immediately
    const paymentData = {
      billId: paymentForm.billId,
      amount: parseFloat(paymentForm.amount).toString(),
      paymentMethod: paymentForm.paymentMethod,
      payerBankAccountId: paymentForm.payerBankAccountId || null,
      receiverBankAccountId: paymentForm.receiverBankAccountId || null,
      receiverBankName: paymentForm.receiverBankName || null,
      receiverAccountNumber: paymentForm.receiverAccountNumber || null,
      receiverRoutingNumber: paymentForm.receiverRoutingNumber || null,
      notes: paymentForm.notes || null
    };
    
    processPaymentMutation.mutate(paymentData);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'captured': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'matched': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-emerald-100 text-emerald-800';
      case 'paid': return 'bg-gray-100 text-gray-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <MoonLoader size="lg" color="teal" />
          <p className="text-muted-foreground mt-4">Loading accounts payable...</p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <style>{`
        .sonner-toaster {
          z-index: 9999 !important;
        }
        .sonner-toast {
          z-index: 9999 !important;
        }
      `}</style>
      <div className="space-y-6 pr-12">
        {/* Page Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-100 shadow-sm">
          <div>
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"><TranslatedText text="UrutiIQ Accounts Payable" /></h1>
            <p className="text-muted-foreground mt-1 text-lg"><TranslatedText text="Comprehensive invoice processing, matching, approval, and payment management" /></p>
          </div>
        </div>
        {/* Dashboard Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Total Invoices" /></CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {dashboardLoading ? (
                <>
                  <Skeleton className="h-8 w-20 mb-2 bg-gray-200" />
                  <Skeleton className="h-4 w-32 bg-gray-200" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">{dashboard.totalInvoices || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    {dashboard.pendingInvoices || 0} <TranslatedText text="pending capture" />
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Outstanding Bills" /></CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {dashboardLoading ? (
                <>
                  <Skeleton className="h-8 w-32 mb-2 bg-gray-200" />
                  <Skeleton className="h-4 w-24 bg-gray-200" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">{dashboard.totalOutstanding?.toLocaleString() || '0'} Frw</div>
                  <p className="text-xs text-muted-foreground">
                    {dashboard.overdueBills || 0} <TranslatedText text="overdue" />
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Vendors" /></CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {dashboardLoading ? (
                <>
                  <Skeleton className="h-8 w-16 mb-2 bg-gray-200" />
                  <Skeleton className="h-4 w-28 bg-gray-200" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">{dashboard.totalVendors || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Active suppliers" />
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium"><TranslatedText text="Processing Rate" /></CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {dashboardLoading ? (
                <>
                  <Skeleton className="h-8 w-16 mb-2 bg-gray-200" />
                  <Skeleton className="h-4 w-32 bg-gray-200" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold">
                    {dashboard.totalInvoices > 0 
                      ? Math.round((dashboard.approvedInvoices / dashboard.totalInvoices) * 100)
                      : 0}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Invoices approved" />
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">{overviewLabel}</TabsTrigger>
            <TabsTrigger value="invoices">{invoiceCaptureLabel}</TabsTrigger>
            <TabsTrigger value="payments">{paymentManagementLabel}</TabsTrigger>
            <TabsTrigger value="reports">{reportsAnalyticsLabel}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Bills */}
              <Card>
                <CardHeader>
                  <CardTitle><TranslatedText text="Recent Bills" /></CardTitle>
                  <CardDescription><TranslatedText text="Latest bills" /> ({bills.length} <TranslatedText text="total" />)</CardDescription>
                </CardHeader>
                <CardContent>
                  {billsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <MoonLoader size="md" color="teal" />
                        <p className="text-muted-foreground mt-4"><TranslatedText text="Loading bills..." /></p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {bills.slice(0, 5).map((bill: any) => (
                        <div key={bill.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <p className="font-medium">{bill.billNumber}</p>
                            <p className="text-sm text-muted-foreground">{bill.vendor?.name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{Number(bill.totalAmount).toLocaleString()} Frw</p>
                            <Badge className={getStatusColor(bill.status)}>
                              <TranslatedText text={bill.status} />
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {bills.length === 0 && !billsLoading && (
                        <div className="text-center py-8 text-muted-foreground">
                          <TranslatedText text="No bills found" />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Aging Summary */}
              <Card>
                <CardHeader>
                  <CardTitle><TranslatedText text="AP Aging Summary" /></CardTitle>
                  <CardDescription><TranslatedText text="Outstanding amounts by age" /></CardDescription>
                </CardHeader>
                <CardContent>
                  {!agingReport.agingSummary || Object.keys(agingReport.agingSummary).length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <BarChart3 className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2"><TranslatedText text="No Aging Data" /></h3>
                      <p className="text-gray-500 text-sm max-w-md mx-auto">
                        <TranslatedText text="There are no outstanding bills to display in the aging summary. Aging data will appear here once you have bills with outstanding balances." />
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(agingReport.agingSummary).map(([bucket, data]: [string, any]) => (
                        <div key={bucket} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <p className="font-medium capitalize">{bucket.replace('_', ' ')}</p>
                            <p className="text-sm text-muted-foreground">{data.count} <TranslatedText text="bills" /></p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{data.totalAmount?.toLocaleString() || '0'} Frw</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Invoice Capture Tab */}
          <TabsContent value="invoices" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex space-x-2">
                <Input
                  placeholder={searchInvoicesPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                    <SelectItem value="captured"><TranslatedText text="Captured" /></SelectItem>
                    <SelectItem value="processing"><TranslatedText text="Processing" /></SelectItem>
                    <SelectItem value="matched"><TranslatedText text="Matched" /></SelectItem>
                    <SelectItem value="approved"><TranslatedText text="Approved" /></SelectItem>
                    <SelectItem value="paid"><TranslatedText text="Paid" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={() => {
                  resetInvoiceForm(); // Clear form when creating new invoice
                  setIsInvoiceDialogOpen(true);
                }}
                disabled={captureInvoiceMutation.isPending || updateInvoiceMutation.isPending}
              >
                {captureInvoiceMutation.isPending || updateInvoiceMutation.isPending ? (
                  <>
                    <MoonLoader size="sm" className="mr-2" color="teal" />
                    {updateInvoiceMutation.isPending ? <TranslatedText text="Updating..." /> : <TranslatedText text="Capturing..." />}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="Capture Invoice" />
                  </>
                )}
              </Button>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead><TranslatedText text="Invoice #" /></TableHead>
                    <TableHead><TranslatedText text="Vendor" /></TableHead>
                    <TableHead><TranslatedText text="Date" /></TableHead>
                    <TableHead><TranslatedText text="Amount" /></TableHead>
                    <TableHead><TranslatedText text="Status" /></TableHead>
                    <TableHead><TranslatedText text="PO Reference" /></TableHead>
                    <TableHead><TranslatedText text="Source" /></TableHead>
                    <TableHead><TranslatedText text="Actions" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoicesLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <MoonLoader size="md" color="teal" />
                          <span className="ml-3 text-muted-foreground"><TranslatedText text="Loading invoices..." /></span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        {invoices.length === 0 ? <><TranslatedText text="No invoices found. Click" /> "<TranslatedText text="Capture Invoice" />" <TranslatedText text="to add one." /></> : <TranslatedText text="No invoices match your filters." />}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInvoices.map((invoice: any) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.vendor?.name}</TableCell>
                        <TableCell>{format(new Date(invoice.invoiceDate), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{Number(invoice.totalAmount).toLocaleString()} Frw</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(invoice.status)}>
                            <TranslatedText text={invoice.status} />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {invoice.rawData && JSON.parse(invoice.rawData || '{}').purchaseOrderId ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              PO-{JSON.parse(invoice.rawData).purchaseOrderId}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm"><TranslatedText text="No PO" /></span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{invoice.source}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            {/* Show different actions based on invoice status */}
                            {invoice.status === 'captured' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    populateInvoiceForm(invoice);
                                    setIsInvoiceDialogOpen(true);
                                  }}
                                  title={tSync("Edit Invoice")}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (confirm(tSync(`Are you sure you want to delete invoice ${invoice.invoiceNumber}? This action cannot be undone.`))) {
                                      deleteInvoiceMutation.mutate(invoice.id);
                                    }
                                  }}
                                  disabled={deleteInvoiceMutation.isPending}
                                  title={tSync("Delete Invoice")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {/* Only show Match button if no purchase order is already selected */}
                            {!invoice.rawData || !JSON.parse(invoice.rawData || '{}').purchaseOrderId ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setIsMatchingDialogOpen(true);
                                }}
                                title={tSync("Match Invoice")}
                              >
                                <FileCheck className="h-4 w-4" />
                              </Button>
                            ) : (
                              <div className="flex items-center space-x-1 text-green-600" title={tSync("Already matched with Purchase Order")}>
                                <FileCheck className="h-4 w-4" />
                                <span className="text-xs"><TranslatedText text="Matched" /></span>
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setIsApprovalDialogOpen(true);
                              }}
                              title={tSync("Approve Invoice")}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Payment Management Tab */}
          <TabsContent value="payments" className="space-y-4">
            {/* Payment Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium"><TranslatedText text="Total Paid" /></CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{paymentStats.totalPaid?.toLocaleString() || '0'} Frw</div>
                  <p className="text-xs text-muted-foreground">
                    {paymentStats.totalPayments || 0} <TranslatedText text="payments made" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium"><TranslatedText text="Scheduled" /></CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{paymentStats.totalScheduled?.toLocaleString() || '0'} Frw</div>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Upcoming payments" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium"><TranslatedText text="Bank Balance" /></CardTitle>
                  <Wallet className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{paymentStats.totalBankBalance?.toLocaleString() || '0'} Frw</div>
                  <p className="text-xs text-muted-foreground">
                    {paymentStats.bankAccounts || 0} <TranslatedText text="accounts" />
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium"><TranslatedText text="Recent Payments" /></CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{payments.length}</div>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Payment records" />
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex space-x-2">
                <Input
                  placeholder={searchBillsPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                    <SelectItem value="posted"><TranslatedText text="Posted" /></SelectItem>
                    <SelectItem value="partially_paid"><TranslatedText text="Partially Paid" /></SelectItem>
                    <SelectItem value="paid"><TranslatedText text="Paid" /></SelectItem>
                    <SelectItem value="overdue"><TranslatedText text="Overdue" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bills Table */}
            <Card>
              <CardHeader>
                <CardTitle><TranslatedText text="All Bills" /> ({bills.length} <TranslatedText text="total" />)</CardTitle>
                <CardDescription><TranslatedText text="Showing" /> {filteredBills.length} <TranslatedText text="bills after filtering" /></CardDescription>
              </CardHeader>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead><TranslatedText text="Bill #" /></TableHead>
                    <TableHead><TranslatedText text="Vendor" /></TableHead>
                    <TableHead><TranslatedText text="Due Date" /></TableHead>
                    <TableHead><TranslatedText text="Amount" /></TableHead>
                    <TableHead><TranslatedText text="Balance Due" /></TableHead>
                    <TableHead><TranslatedText text="PO Reference" /></TableHead>
                    <TableHead><TranslatedText text="Status" /></TableHead>
                    <TableHead><TranslatedText text="Actions" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billsLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <MoonLoader size="md" color="teal" />
                          <span className="ml-3 text-muted-foreground"><TranslatedText text="Loading bills..." /></span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        {bills.length === 0 ? <TranslatedText text="No bills found." /> : <TranslatedText text="No bills match your filters." />}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBills.map((bill: any) => (
                      <TableRow key={bill.id}>
                        <TableCell className="font-medium">{bill.billNumber}</TableCell>
                        <TableCell>{bill.vendor?.name}</TableCell>
                        <TableCell>
                          {bill.dueDate ? format(new Date(bill.dueDate), 'MMM dd, yyyy') : <TranslatedText text="N/A" />}
                        </TableCell>
                        <TableCell>{Number(bill.totalAmount).toLocaleString()} Frw</TableCell>
                        <TableCell>{Number(bill.balanceDue).toLocaleString()} Frw</TableCell>
                        <TableCell>
                          {bill.invoiceCapture?.rawData && JSON.parse(bill.invoiceCapture.rawData || '{}').purchaseOrderId ? (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              PO-{JSON.parse(bill.invoiceCapture.rawData).purchaseOrderId}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm"><TranslatedText text="No PO" /></span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(bill.status)}>
                            <TranslatedText text={bill.status} />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={async () => {
                                setPaymentForm({
                                  ...paymentForm,
                                  billId: bill.id,
                                  amount: Number(bill.balanceDue).toString(),
                                  paymentMethod: 'bank_transfer',
                                  scheduledDate: new Date().toISOString().split('T')[0]
                                });
                                setIsPaymentDialogOpen(true);
                                await loadBankAccounts();
                              }}
                              disabled={bill.status === 'paid' || bill.balanceDue <= 0 || processingBillId === bill.id}
                              title={bill.status === 'paid' ? tSync('Already paid') : tSync('Pay now')}
                            >
                              {processingBillId === bill.id ? (
                                <>
                                  <MoonLoader size="sm" className="mr-2" color="teal" />
                                  <TranslatedText text="Processing..." />
                                </>
                              ) : (
                                <>
                                  <CreditCard className="h-4 w-4 mr-1" />
                                  <TranslatedText text="Pay Now" />
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                setSelectedBillForView(bill);
                                setShowBillViewDialog(true);
                                
                                // Fetch reconciliation status
                                try {
                                  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
                                  const tenantId = localStorage.getItem('tenant_id') || 'tenant_demo';
                                  const API = getApiBaseUrl();
                                  
                                  const response = await fetch(`${API}/api/bills/${bill.id}/payments`, {
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'x-tenant-id': tenantId,
                                      'x-company-id': firstCompanyId
                                    }
                                  });
                                  
                                  if (response.ok) {
                                    const payments = await response.json();
                                    const reconciledPayments = payments.filter((p: any) => p.bankTransactionId || p.isReconciled);
                                    const bankTransactions = reconciledPayments.map((p: any) => ({
                                      id: p.bankTransactionId,
                                      amount: p.amount,
                                      date: p.paymentDate,
                                      description: p.reference || 'Bank Transaction'
                                    }));
                                    
                                    setBillReconciliationStatus({
                                      isReconciled: reconciledPayments.length > 0,
                                      reconciledPayments: reconciledPayments.length,
                                      totalPayments: payments.length,
                                      bankTransactions
                                    });
                                  } else {
                                    setBillReconciliationStatus(null);
                                  }
                                } catch {
                                  setBillReconciliationStatus(null);
                                }
                              }}
                              title={tSync("View bill details")}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>

            {/* Recent Payments */}
            <Card>
              <CardHeader>
                <CardTitle><TranslatedText text="Recent Payments" /></CardTitle>
                <CardDescription><TranslatedText text="Latest payment transactions" /></CardDescription>
              </CardHeader>
              <CardContent>
                {paymentDataLoading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <MoonLoader size="md" color="teal" />
                    <p className="text-muted-foreground mt-4">Loading payments...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {payments.slice(0, 5).map((payment: any) => (
                      <div key={payment.id} className="flex items-center justify-between p-2 border rounded">
                        <div>
                          <p className="font-medium">{payment.bill?.billNumber}</p>
                          <p className="text-sm text-muted-foreground">{payment.bill?.vendor?.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{Number(payment.amount).toLocaleString()} Frw</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    ))}
                    {payments.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground">
                        No payments found
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Payments */}
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Payments</CardTitle>
                <CardDescription>Scheduled payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {paymentDataLoading ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <MoonLoader size="md" color="teal" />
                    <p className="text-muted-foreground mt-4">Loading upcoming payments...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {paymentSchedules.slice(0, 5).map((schedule: any) => (
                      <div key={schedule.id} className="flex items-center justify-between p-2 border rounded">
                        <div>
                          <p className="font-medium">{schedule.bill?.billNumber || schedule.purchaseOrder?.poNumber || 'N/A'}</p>
                          <p className="text-sm text-muted-foreground">{schedule.bill?.vendor?.name || schedule.purchaseOrder?.vendor?.name || 'N/A'}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{Number(schedule.amount).toLocaleString()} Frw</p>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(schedule.scheduledDate), 'MMM dd, yyyy')}
                          </p>
                        </div>
                      </div>
                    ))}
                    {paymentSchedules.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground">
                        <TranslatedText text="No scheduled payments" />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle><TranslatedText text="AP Aging Report" /></CardTitle>
                  <CardDescription><TranslatedText text="Outstanding amounts by aging buckets" /></CardDescription>
                </CardHeader>
                <CardContent>
                  {!agingReport.agingSummary || Object.keys(agingReport.agingSummary).length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <BarChart3 className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2"><TranslatedText text="No Aging Data" /></h3>
                      <p className="text-gray-500 text-sm max-w-md mx-auto">
                        <TranslatedText text="There are no outstanding bills to display in the aging report. Aging data will appear here once you have bills with outstanding balances." />
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(agingReport.agingSummary).map(([bucket, data]: [string, any]) => (
                        <div key={bucket} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium capitalize">{bucket.replace('_', ' ')}</p>
                            <p className="text-sm text-muted-foreground">{data.count} bills</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{data.totalAmount?.toLocaleString() || '0'} Frw</p>
                          </div>
                        </div>
                      ))}
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between">
                          <p className="font-bold">Total Outstanding</p>
                          <p className="text-xl font-bold">{agingReport.totalOutstanding?.toLocaleString() || '0'} Frw</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Processing Statistics</CardTitle>
                  <CardDescription>Invoice processing metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span>Total Invoices</span>
                      <span className="font-bold">{dashboard.totalInvoices || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Pending Processing</span>
                      <span className="font-bold">{dashboard.pendingInvoices || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Matched</span>
                      <span className="font-bold">{dashboard.matchedInvoices || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Approved</span>
                      <span className="font-bold">{dashboard.approvedInvoices || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Processing Rate</span>
                      <span className="font-bold">
                        {dashboard.totalInvoices > 0 
                          ? Math.round((dashboard.approvedInvoices / dashboard.totalInvoices) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        {/* Invoice Capture Dialog */}
        <Dialog open={isInvoiceDialogOpen} onOpenChange={(open) => {
          setIsInvoiceDialogOpen(open);
          if (!open) {
            resetInvoiceForm(); // Clear form when dialog is closed
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedInvoice ? 'Edit Invoice' : 'Capture New Invoice'}</DialogTitle>
              <DialogDescription>
                {selectedInvoice ? 'Update invoice details' : 'Create an invoice for a purchase order to process payment'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Purchase Order</label>
                <Select value={invoiceForm.purchaseOrderId} onValueChange={(value) => {
                  const selectedPO = purchaseOrders.find((po: any) => po.id === value);
                  // Format the total amount to avoid floating-point precision issues
                  const formattedAmount = selectedPO?.totalAmount 
                    ? parseFloat(selectedPO.totalAmount).toFixed(2)
                    : invoiceForm.totalAmount;
                  
                  // Calculate subtotal when total amount changes
                  const taxPercentage = invoiceForm.taxAmount ? parseFloat(invoiceForm.taxAmount) : 0;
                  const subtotal = parseFloat(formattedAmount) / (1 + (taxPercentage / 100));
                  
                  // Auto-generate invoice number from purchase order number
                  // Format: INV-{PO_NUMBER} or use PO number directly if it already starts with INV
                  let generatedInvoiceNumber = '';
                  if (selectedPO?.poNumber) {
                    const poNumber = selectedPO.poNumber;
                    if (poNumber.toUpperCase().startsWith('INV-')) {
                      // If PO number already starts with INV-, use it as is
                      generatedInvoiceNumber = poNumber;
                    } else if (poNumber.toUpperCase().startsWith('PO-')) {
                      // Convert PO-XXX to INV-XXX
                      generatedInvoiceNumber = poNumber.replace(/^PO-/i, 'INV-');
                    } else {
                      // Add INV- prefix if it doesn't have one
                      generatedInvoiceNumber = `INV-${poNumber}`;
                    }
                  }
                  
                  console.log('🔍 PO Selection - Tax calc:', {
                    taxAmount: invoiceForm.taxAmount,
                    taxPercentage,
                    formattedAmount,
                    subtotal: subtotal.toFixed(2),
                    generatedInvoiceNumber
                  });
                  
                  setInvoiceForm({
                    ...invoiceForm, 
                    purchaseOrderId: value,
                    vendorId: selectedPO?.vendorId || selectedPO?.vendor?.id || invoiceForm.vendorId, // Prefill vendor from purchase order
                    totalAmount: formattedAmount,
                    subtotal: subtotal.toFixed(2),
                    invoiceNumber: generatedInvoiceNumber || invoiceForm.invoiceNumber // Prefill invoice number from purchase order
                  });
                  
                  // Clear vendor error if vendor was prefilled
                  if (selectedPO?.vendorId || selectedPO?.vendor?.id) {
                    if (formErrors.vendorId) {
                      setFormErrors({...formErrors, vendorId: ''});
                    }
                  }
                  
                  // Clear invoice number error if invoice number was prefilled
                  if (generatedInvoiceNumber && formErrors.invoiceNumber) {
                    setFormErrors({...formErrors, invoiceNumber: ''});
                  }
                  
                  if (formErrors.purchaseOrderId) {
                    setFormErrors({...formErrors, purchaseOrderId: ''});
                  }
                }}>
                  <SelectTrigger className={formErrors.purchaseOrderId ? 'border-red-500' : ''}>
                    <SelectValue placeholder={purchaseOrdersLoading ? "Loading purchase orders..." : "Select purchase order"}>
                      {invoiceForm.purchaseOrderId ? 
                        purchaseOrders.find((po: any) => po.id === invoiceForm.purchaseOrderId)?.poNumber || "Select purchase order"
                        : "Select purchase order"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {purchaseOrdersLoading ? (
                      <SelectItem value="loading" disabled>Loading purchase orders...</SelectItem>
                    ) : !Array.isArray(purchaseOrders) || purchaseOrders.length === 0 ? (
                      <SelectItem value="no-pos" disabled>No purchase orders found - Create one first</SelectItem>
                    ) : (() => {
                      const filteredPOs = purchaseOrders.filter((po: any) => {
                        // Exclude closed or cancelled purchase orders
                        if (po.status === 'closed' || po.status === 'cancelled') {
                          return false;
                        }
                        // Only show purchase orders with approved or delivered status
                        if (!['approved', 'delivered'].includes(po.status)) {
                          return false;
                        }
                        // Exclude purchase orders that have been converted to bills (have relatedBillId or relatedBill)
                        if (po.relatedBillId || po.relatedBill) {
                          return false;
                        }
                        return true;
                      });
                      
                      if (filteredPOs.length === 0) {
                        return <SelectItem value="no-pos" disabled>No unpaid purchase orders found</SelectItem>;
                      }
                      
                      return filteredPOs.map((po: any) => (
                        <SelectItem key={po.id} value={po.id}>
                          {po.poNumber} - {po.vendor?.name} ({parseFloat(po.totalAmount).toLocaleString()} Frw) - {po.status}
                        </SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                {formErrors.purchaseOrderId && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.purchaseOrderId}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Select a purchase order to automatically populate vendor and amount
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Vendor</label>
                  {(!Array.isArray(vendors) || vendors.length === 0) && !vendorsLoading && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsVendorModalOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Create Vendor
                    </Button>
                  )}
                </div>
                <Select 
                  value={invoiceForm.vendorId} 
                  onValueChange={(value) => {
                    if (value === '__create_new__') {
                      setIsVendorModalOpen(true)
                    } else {
                      setInvoiceForm({...invoiceForm, vendorId: value});
                      if (formErrors.vendorId) {
                        setFormErrors({...formErrors, vendorId: ''});
                      }
                    }
                  }}
                  disabled={!!invoiceForm.purchaseOrderId} // Disable vendor selection when purchase order is selected
                >
                  <SelectTrigger className={formErrors.vendorId ? 'border-red-500' : ''}>
                    <SelectValue placeholder={vendorsLoading ? "Loading vendors..." : invoiceForm.purchaseOrderId ? "Vendor from Purchase Order" : "Select vendor"} />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorsLoading ? (
                      <SelectItem value="loading-vendors" disabled>Loading vendors...</SelectItem>
                    ) : (
                      <>
                        <SelectItem 
                          value="__create_new__"
                          className="text-primary font-medium cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <Plus className="w-4 h-4 text-primary" />
                            <span>Create New Vendor</span>
                          </div>
                        </SelectItem>
                        {!Array.isArray(vendors) || vendors.length === 0 ? (
                          <SelectItem value="no-vendors" disabled>No vendors found</SelectItem>
                        ) : (
                          vendors.map((vendor: any) => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))
                        )}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {formErrors.vendorId && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.vendorId}</p>
                )}
                {vendorsError && (
                  <p className="text-sm text-red-500 mt-1">Error loading vendors: {vendorsError.message}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Invoice Number</label>
                <Input
                  value={invoiceForm.invoiceNumber}
                  onChange={(e) => {
                    setInvoiceForm({...invoiceForm, invoiceNumber: e.target.value});
                    if (formErrors.invoiceNumber) {
                      setFormErrors({...formErrors, invoiceNumber: ''});
                    }
                  }}
                  placeholder="Enter invoice number"
                  className={formErrors.invoiceNumber ? 'border-red-500' : ''}
                />
                {formErrors.invoiceNumber && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.invoiceNumber}</p>
                )}
                {invoiceForm.purchaseOrderId && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto-filled from purchase order (you can edit if needed)
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Invoice Date</label>
                <Input
                  type="date"
                  value={invoiceForm.invoiceDate}
                  onChange={(e) => {
                    setInvoiceForm({...invoiceForm, invoiceDate: e.target.value});
                    if (formErrors.invoiceDate) {
                      setFormErrors({...formErrors, invoiceDate: ''});
                    }
                  }}
                  className={formErrors.invoiceDate ? 'border-red-500' : ''}
                />
                {formErrors.invoiceDate && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.invoiceDate}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <Input
                  type="date"
                  value={invoiceForm.dueDate}
                  onChange={(e) => setInvoiceForm({...invoiceForm, dueDate: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Tax Percentage (Optional)</label>
                <div className="relative">
                  <Input
                    type="number"
                    value={invoiceForm.taxAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      const taxPercentage = value ? parseFloat(value) : 0;
                      const totalAmount = parseFloat(invoiceForm.totalAmount) || 0;
                      
                      // Calculate subtotal: Total / (1 + Tax%)
                      const subtotal = totalAmount / (1 + (taxPercentage / 100));
                      
                      console.log('🔍 Tax Input - Tax calc:', {
                        value,
                        taxPercentage,
                        totalAmount,
                        subtotal: subtotal.toFixed(2)
                      });
                      
                      setInvoiceForm({
                        ...invoiceForm, 
                        taxAmount: value, // Store the raw input value
                        subtotal: subtotal.toFixed(2)
                      });
                      if (formErrors.taxAmount) {
                        setFormErrors({...formErrors, taxAmount: ''});
                      }
                    }}
                    placeholder="0.00"
                    className={`text-right font-mono ${formErrors.taxAmount ? 'border-red-500' : ''}`}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
                {formErrors.taxAmount && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.taxAmount}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Subtotal (Calculated)</label>
                <Input
                  type="number"
                  value={invoiceForm.subtotal}
                  disabled
                  className="text-right font-mono bg-gray-50 text-gray-700"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Calculated as: Total ÷ (1 + Tax%)</p>
              </div>
              <div>
                <label className="text-sm font-medium">Total Amount (From Purchase Order)</label>
                <Input
                  type="number"
                  value={invoiceForm.totalAmount}
                  disabled
                  className="text-right font-mono bg-gray-50 text-gray-700"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Amount comes from selected purchase order</p>
              </div>
              <div>
                <label className="text-sm font-medium">Currency</label>
                <Select value={invoiceForm.currency} onValueChange={(value) => setInvoiceForm({...invoiceForm, currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Frw">Frw</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Source</label>
                <Select value={invoiceForm.source} onValueChange={(value) => setInvoiceForm({...invoiceForm, source: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Entry</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="ocr">OCR Scan</SelectItem>
                    <SelectItem value="upload">File Upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Notes <span className="text-red-500">*</span></label>
                <Input
                  value={invoiceForm.notes}
                  onChange={(e) => {
                    setInvoiceForm({...invoiceForm, notes: e.target.value});
                    if (formErrors.notes) {
                      setFormErrors({...formErrors, notes: ''});
                    }
                  }}
                  placeholder="Enter notes (required)"
                  className={formErrors.notes ? 'border-red-500' : ''}
                />
                {formErrors.notes && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.notes}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsInvoiceDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCaptureInvoice} 
                disabled={captureInvoiceMutation.isPending || updateInvoiceMutation.isPending}
              >
                {captureInvoiceMutation.isPending || updateInvoiceMutation.isPending ? (
                  <>
                    <MoonLoader size="sm" className="mr-2" color="teal" />
                    {selectedInvoice ? 'Updating...' : 'Capturing...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {selectedInvoice ? 'Update Invoice' : 'Capture Invoice'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Matching Dialog */}
        <Dialog open={isMatchingDialogOpen} onOpenChange={setIsMatchingDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Match Invoice</DialogTitle>
              <DialogDescription>
                Match invoice with purchase order and goods received note
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Matching Type</label>
                <Select value={matchingForm.matchingType} onValueChange={(value) => setMatchingForm({...matchingForm, matchingType: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="two_way">Two-way (Invoice + PO)</SelectItem>
                    <SelectItem value="three_way">Three-way (Invoice + PO + GRN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Purchase Order</label>
                <Select value={matchingForm.purchaseOrderId} onValueChange={(value) => setMatchingForm({...matchingForm, purchaseOrderId: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select PO" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Add PO options here */}
                  </SelectContent>
                </Select>
              </div>
              {matchingForm.matchingType === 'three_way' && (
                <div>
                  <label className="text-sm font-medium">Goods Received Note</label>
                  <Select value={matchingForm.goodsReceivedNoteId} onValueChange={(value) => setMatchingForm({...matchingForm, goodsReceivedNoteId: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select GRN" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Add GRN options here */}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsMatchingDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleMatchInvoice} disabled={matchInvoiceMutation.isPending}>
                {matchInvoiceMutation.isPending ? 'Matching...' : 'Match Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Approval Dialog */}
        <Dialog open={isApprovalDialogOpen} onOpenChange={setIsApprovalDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Invoice</DialogTitle>
              <DialogDescription>
                Review and approve or reject the invoice
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Approver ID</label>
                <Input
                  value={approvalForm.approverId}
                  onChange={(e) => setApprovalForm({...approvalForm, approverId: e.target.value})}
                  placeholder="Enter approver ID"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Approval Level</label>
                <Select value={approvalForm.approvalLevel.toString()} onValueChange={(value) => setApprovalForm({...approvalForm, approvalLevel: parseInt(value)})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Level 1</SelectItem>
                    <SelectItem value="2">Level 2</SelectItem>
                    <SelectItem value="3">Level 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Decision</label>
                <Select value={approvalForm.status} onValueChange={(value) => setApprovalForm({...approvalForm, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approve</SelectItem>
                    <SelectItem value="rejected">Reject</SelectItem>
                    <SelectItem value="delegated">Delegate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Comments</label>
                <Input
                  value={approvalForm.comments}
                  onChange={(e) => setApprovalForm({...approvalForm, comments: e.target.value})}
                  placeholder="Approval comments"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsApprovalDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApproveInvoice} disabled={approveInvoiceMutation.isPending}>
                {approveInvoiceMutation.isPending ? 'Processing...' : 'Submit Decision'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Schedule Dialog - Redesigned */}
        <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="pb-6 flex-shrink-0">
              <DialogTitle className="text-2xl font-semibold text-slate-900">Process Payment</DialogTitle>
              <DialogDescription className="text-slate-600">
                Process payment for the selected bill with complete bank account information
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 sm:space-y-8 overflow-y-auto flex-1 pr-2">
              {/* Payment Details Section */}
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Payment Details</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Scheduled Date
                    </label>
                    <Input
                      type="date"
                      value={paymentForm.scheduledDate}
                      onChange={(e) => setPaymentForm({...paymentForm, scheduledDate: e.target.value})}
                      className="h-11"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-medium z-10">Frw</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={paymentForm.amount}
                        onChange={(e) => {
                          // Remove currency symbols and non-numeric characters except decimal point
                          let cleanedValue = e.target.value
                            .replace(/[FrwRWF\s,]/gi, '') // Remove currency symbols and commas
                            .replace(/[^0-9.]/g, '') // Keep only numbers and decimal point
                            .replace(/(\..*)\./g, '$1'); // Prevent multiple decimal points
                          
                          // Update form with cleaned value
                          setPaymentForm({...paymentForm, amount: cleanedValue});
                        }}
                        onBlur={(e) => {
                          // Format the value on blur to ensure proper decimal format
                          const numValue = parseFloat(e.target.value);
                          if (!isNaN(numValue) && numValue >= 0) {
                            setPaymentForm({...paymentForm, amount: numValue.toString()});
                          } else if (e.target.value === '' || e.target.value === '.') {
                            setPaymentForm({...paymentForm, amount: ''});
                          }
                        }}
                        placeholder="0.00"
                        className="h-11 pl-12"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Section */}
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Payment Method</h3>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Payment Method <span className="text-red-500">*</span></label>
                  <Select value={paymentForm.paymentMethod} onValueChange={(value) => setPaymentForm({...paymentForm, paymentMethod: value})}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          Bank Transfer
                        </div>
                      </SelectItem>
                      <SelectItem value="check">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Check
                        </div>
                      </SelectItem>
                      <SelectItem value="credit_card">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          Credit Card
                        </div>
                      </SelectItem>
                      <SelectItem value="cash">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          Cash
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bank Account Information */}
              {['bank_transfer', 'check'].includes(paymentForm.paymentMethod) && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Wallet className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold text-slate-900">Bank Account Information</h3>
                  </div>
                  
                  <div className="space-y-4 sm:space-y-6">
                    {/* Payer Bank Account */}
                    {availableBankAccounts.length > 0 && (
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          Payer Bank Account (Your Company's Bank) <span className="text-red-500">*</span>
                        </label>
                        <Select 
                          value={paymentForm.payerBankAccountId} 
                          onValueChange={(value) => setPaymentForm({...paymentForm, payerBankAccountId: value})}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select your company's bank account" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableBankAccounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                <div className="flex flex-col py-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-semibold text-slate-900">{account.bankName}</span>
                                    {account.balance && (
                                      <span className="text-sm font-medium text-green-600">
                                        {Number(account.balance).toLocaleString()} Frw
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-sm text-slate-500">
                                    {account.accountNumber} • {account.accountType || 'Account'}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Balance Validation Indicator */}
                        {paymentForm.payerBankAccountId && paymentForm.amount && (
                          <div className="mt-2">
                            {(() => {
                              const selectedAccount = availableBankAccounts.find(account => account.id === paymentForm.payerBankAccountId);
                              const accountBalance = selectedAccount?.balance ? Number(selectedAccount.balance) : 0;
                              const paymentAmount = Number(paymentForm.amount);
                              const hasSufficientBalance = accountBalance >= paymentAmount;
                              
                              return (
                                <div className={`p-3 rounded-lg border ${
                                  hasSufficientBalance 
                                    ? 'bg-green-50 border-green-200' 
                                    : 'bg-red-50 border-red-200'
                                }`}>
                                  <div className="flex items-center gap-2">
                                    {hasSufficientBalance ? (
                                      <CheckCircle className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <AlertCircle className="w-4 h-4 text-red-600" />
                                    )}
                                    <div className="text-sm">
                                      <span className={`font-medium ${
                                        hasSufficientBalance ? 'text-green-800' : 'text-red-800'
                                      }`}>
                                        {hasSufficientBalance ? 'Sufficient Balance' : 'Insufficient Balance'}
                                      </span>
                                      <div className={`text-xs ${
                                        hasSufficientBalance ? 'text-green-700' : 'text-red-700'
                                      }`}>
                                        Available: {accountBalance.toLocaleString()} Frw | Required: {paymentAmount.toLocaleString()} Frw
                                        {!hasSufficientBalance && (
                                          <span className="block mt-1">
                                            Shortfall: {(paymentAmount - accountBalance).toLocaleString()} Frw
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Receiver Bank Account */}
                    <div className="space-y-3">
                      <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Receiver Bank Account (Vendor's Bank) <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Bank Name</label>
                          <Select 
                            value={paymentForm.receiverBankName} 
                            onValueChange={(value) => setPaymentForm({...paymentForm, receiverBankName: value})}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue placeholder="Search and select bank..." />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              <div className="p-2 border-b">
                                <Input
                                  placeholder="Search banks..."
                                  value={bankSearchTerm}
                                  onChange={(e) => handleBankSearch(e.target.value)}
                                  className="h-8"
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {filteredBanks.length > 0 ? (
                                  filteredBanks.map((bank) => (
                                    <SelectItem key={bank} value={bank}>
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <span>{bank}</span>
                                      </div>
                                    </SelectItem>
                                  ))
                                ) : (
                                  <div className="p-2 text-sm text-slate-500 text-center">
                                    No banks found matching "{bankSearchTerm}"
                                  </div>
                                )}
                              </div>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Account Number</label>
                          <Input
                            placeholder="Account number"
                            value={paymentForm.receiverAccountNumber}
                            onChange={(e) => setPaymentForm({...paymentForm, receiverAccountNumber: e.target.value})}
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Routing Number (Optional)</label>
                        <Input
                          placeholder="Routing number"
                          value={paymentForm.receiverRoutingNumber}
                          onChange={(e) => setPaymentForm({...paymentForm, receiverRoutingNumber: e.target.value})}
                          className="h-10"
                        />
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium">Bank Account Requirements</p>
                            <p className="text-blue-700">Select bank name from the list and enter account number. Bank name and account number are required for bank transfers and checks.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Settings */}
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                  <Settings className="w-5 h-5 text-slate-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Additional Settings</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Priority Level</label>
                    <Select value={paymentForm.priority} onValueChange={(value) => setPaymentForm({...paymentForm, priority: value})}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            Low Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="normal">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            Normal Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="high">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            High Priority
                          </div>
                        </SelectItem>
                        <SelectItem value="urgent">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            Urgent Priority
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Payment Notes</label>
                    <Input
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                      placeholder="Add payment notes or reference"
                      className="h-11"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="pt-6 border-t border-slate-200 flex-shrink-0">
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button 
                  variant="outline" 
                  onClick={() => setIsPaymentDialogOpen(false)}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  onClick={handleProcessPayment} 
                  disabled={
                    processPaymentMutation.isPending || 
                    schedulePaymentMutation.isPending ||
                    (['bank_transfer', 'check'].includes(paymentForm.paymentMethod) && 
                     (!paymentForm.payerBankAccountId || !paymentForm.receiverBankName || !paymentForm.receiverAccountNumber)) ||
                    !checkBankAccountBalance()
                  }
                  className="flex-1 h-11 bg-blue-600 hover:bg-blue-700"
                >
                  {processPaymentMutation.isPending ? (
                    <div className="flex items-center gap-2">
                      <MoonLoader size="sm" color="teal" />
                      Processing...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Pay Now
                    </div>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bill Detail View Dialog */}
        <Dialog open={showBillViewDialog} onOpenChange={(open) => {
          setShowBillViewDialog(open);
          if (!open) {
            setSelectedBillForView(null);
            setBillReconciliationStatus(null);
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Bill Details</DialogTitle>
              <DialogDescription className="text-slate-600 mt-1">
                {selectedBillForView ? `Bill ${selectedBillForView.billNumber}` : 'Loading bill details...'}
              </DialogDescription>
            </DialogHeader>
            
            {selectedBillForView && (
              <div className="space-y-6">
                {/* Bill Header */}
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Bill Information</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Bill Number:</span>
                        <span className="font-medium">{selectedBillForView.billNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Bill Date:</span>
                        <span className="font-medium">
                          {selectedBillForView.billDate ? format(new Date(selectedBillForView.billDate), 'MMM dd, yyyy') : 'N/A'}
                        </span>
                      </div>
                      {selectedBillForView.dueDate && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Due Date:</span>
                          <span className="font-medium">{format(new Date(selectedBillForView.dueDate), 'MMM dd, yyyy')}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Status:</span>
                        <Badge className={getStatusColor(selectedBillForView.status)}>
                          {selectedBillForView.status}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Currency:</span>
                        <span className="font-medium">{selectedBillForView.currency || 'USD'}</span>
                      </div>
                      
                      {/* Reconciliation Status */}
                      {billReconciliationStatus && (
                        <div className="pt-3 mt-3 border-t border-slate-300">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">Reconciliation Status:</span>
                            {billReconciliationStatus.isReconciled ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Reconciled
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Not Reconciled</Badge>
                            )}
                          </div>
                          {billReconciliationStatus.isReconciled && (
                            <div className="mt-2 space-y-1">
                              <p className="text-xs text-slate-600">
                                {billReconciliationStatus.reconciledPayments} of {billReconciliationStatus.totalPayments} payment{billReconciliationStatus.totalPayments !== 1 ? 's' : ''} reconciled
                              </p>
                              {billReconciliationStatus.bankTransactions.length > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-200">
                                  <p className="text-xs font-medium text-slate-700 mb-1">Bank Transactions:</p>
                                  {billReconciliationStatus.bankTransactions.map((bt, idx) => (
                                    <div key={idx} className="text-xs text-slate-600 flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3 text-green-600" />
                                      {Number(bt.amount).toLocaleString()} Frw - {format(new Date(bt.date), 'MMM dd, yyyy')}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Vendor Information</h3>
                    {selectedBillForView.vendor ? (
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Name:</span>
                          <span className="font-medium">{selectedBillForView.vendor.name}</span>
                        </div>
                        {selectedBillForView.vendor.email && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Email:</span>
                            <span className="font-medium">{selectedBillForView.vendor.email}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">Vendor information not available</p>
                    )}
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Financial Summary</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Total Amount:</span>
                        <span className="font-semibold text-lg">{Number(selectedBillForView.totalAmount).toLocaleString()} Frw</span>
                      </div>
                      {selectedBillForView.balanceDue > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Balance Due:</span>
                          <span className="font-semibold text-amber-600">{Number(selectedBillForView.balanceDue).toLocaleString()} Frw</span>
                        </div>
                      )}
                      {selectedBillForView.balanceDue === 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Payment Status:</span>
                          <span className="font-semibold text-green-600">Fully Paid</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Vendor Modal */}
        <VendorModal
          isOpen={isVendorModalOpen}
          onClose={() => setIsVendorModalOpen(false)}
          onSubmit={async (vendorData) => {
            await createVendorMutation.mutateAsync(vendorData)
          }}
          companyId={firstCompanyId}
          isLoading={createVendorMutation.isPending}
        />
      </div>
    </PageLayout>
  );
}
